import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FiX, FiCamera, FiSave, FiEye, FiEyeOff } from 'react-icons/fi';
import { FaPaw } from "react-icons/fa";
interface AdminUser {
  id: number;
  email: string;
  name: string;
  plan_type: 'free'| 'free-trial' | 'basic' | 'premium';
  created_at: string;
  usage: {
    text_messages_sent: number;
    voice_minutes_used: number;
    image_uploads: number;
  };
  subscription_status: string;
  avatar_url?: string;
  is_active?: boolean;
  avatar_file?: File;
  new_password?: string;
  user_type: 'user' | 'admin' | 'super-admin'; // ✅ Add this
}


interface UserManagementModalProps {
  user: AdminUser | null;
  onClose: () => void;
  onSave: (updatedUser: Partial<AdminUser>) => Promise<void>;
  loading?: boolean;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #232B3A;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #FF9900;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #FF9900;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #FF9900;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const ToggleTitle = styled.span`
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const ToggleDescription = styled.span`
  color: #6b7280;
  font-size: 0.9rem;
`;

const ToggleSwitch = styled.button<{ active: boolean }>`
  width: 60px;
  height: 32px;
  border-radius: 16px;
  border: none;
  background: ${props => props.active ? '#FF9900' : '#cbd5e1'};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '30px' : '2px'};
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
  }
`;

const AvatarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: visible;
  border: 3px solid #FFEDD5;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    border-color: #FF9900;
    transform: scale(1.02);
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarUploadButton = styled.button`
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: #FF9900;
  color: white;
  border: 2px solid white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  
  &:hover {
    background: #e67e22;
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }
`;

const PasswordInputContainer = styled.div`
  position: relative;
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  
  &:hover {
    color: #374151;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const SaveButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#cbd5e1' : 'linear-gradient(135deg, #FF9900, #FFD700)'};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 153, 0, 0.3);
  }
`;

const CancelButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  border: 1px solid #fecaca;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  color: #16a34a;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  border: 1px solid #bbf7d0;
`;

const UserManagementModal: React.FC<UserManagementModalProps> = ({
  user,
  onClose,
  onSave,
  loading = false
}) => {
const [formData, setFormData] = useState({
  name: user?.name || '',
  email: user?.email || '',
  plan_type: user?.plan_type || 'free',
  user_type: user?.user_type || 'user',   // ✅ add default
  is_active: user?.is_active ?? true,
  new_password: '',
  confirm_password: ''
});
  
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Update form data when user changes
React.useEffect(() => {
  if (user) {
    setFormData({
      name: user.name,
      email: user.email,
      plan_type: user.plan_type,
      user_type: user.user_type,   // ✅ load from user
      is_active: user.is_active ?? true,
      new_password: '',
      confirm_password: ''
    });
    setAvatarFile(null);
    setError('');
    setSuccess('');
  }
}, [user]);


  // Auto-scroll to top when success or error message appears
  React.useEffect(() => {
    if ((success || error) && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [success, error]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file must be smaller than 5MB');
        return;
      }
      setAvatarFile(file);
      setError('');
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    // Validate password if provided
    if (formData.new_password) {
      if (formData.new_password.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        setError('Passwords do not match');
        return;
      }
    }

    try {
const updatedUser: Partial<AdminUser> = {
  name: formData.name,
  email: formData.email,
  plan_type: formData.plan_type as 'free' | 'basic' | 'premium',
  user_type: formData.user_type as 'user' | 'admin' | 'super-admin', // ✅ add this
  is_active: formData.is_active
};


      if (formData.new_password) {
        updatedUser.new_password = formData.new_password;
      }

      if (avatarFile) {
        updatedUser.avatar_file = avatarFile;
      }

      await onSave(updatedUser);
      setSuccess('User updated successfully!');
      
      // Clear password fields after successful save
      setFormData(prev => ({
        ...prev,
        new_password: '',
        confirm_password: ''
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    }
  };

  if (!user) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent ref={modalContentRef} onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Edit User: {user.name}</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <Section>
            <SectionTitle>Profile Information</SectionTitle>
            
            <AvatarSection>
              <AvatarContainer>
                {user.avatar_url || avatarFile ? (
                  <AvatarImage 
                    src={avatarFile ? URL.createObjectURL(avatarFile) : user.avatar_url} 
                    alt={user.name} 
                  />
                ) : (
                  <FaPaw size={40} color="#9ca3af" />
                )}
                <AvatarUploadButton onClick={() => fileInputRef.current?.click()}>
                  <FiCamera size={16} />
                </AvatarUploadButton>
              </AvatarContainer>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              <div>
                <Label>Profile Picture</Label>
                <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>
                  Click the camera icon to upload a new profile picture
                </p>
              </div>
            </AvatarSection>

            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder="Enter user name"
              />
            </FormGroup>

            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                placeholder="Enter user email"
              />
            </FormGroup>

            <FormGroup>
              <Label>Plan Type</Label>
              <Select
                value={formData.plan_type}
                onChange={e => handleInputChange('plan_type', e.target.value)}
              >
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
              </Select>
            </FormGroup>


          </Section>

          <Section>
            <SectionTitle>Account Status</SectionTitle>
            <ToggleContainer>
              <ToggleLabel>
                <ToggleTitle>Account Active</ToggleTitle>
                <ToggleDescription>
                  {formData.is_active ? 'User can log in and use the service' : 'User account is disabled'}
                </ToggleDescription>
              </ToggleLabel>
              <ToggleSwitch
                active={formData.is_active}
                onClick={() => handleInputChange('is_active', !formData.is_active)}
              />
            </ToggleContainer>
          </Section>

          <Section>
            <SectionTitle>Change Password</SectionTitle>
            <FormGroup>
              <Label>New Password</Label>
              <PasswordInputContainer>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={e => handleInputChange('new_password', e.target.value)}
                  placeholder="Leave blank to keep current password"
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggleButton>
              </PasswordInputContainer>
            </FormGroup>

            <FormGroup>
              <Label>Confirm New Password</Label>
              <PasswordInputContainer>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={e => handleInputChange('confirm_password', e.target.value)}
                  placeholder="Confirm new password"
                />
                <PasswordToggleButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </PasswordToggleButton>
              </PasswordInputContainer>
            </FormGroup>
          </Section>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose}>
            Cancel
          </CancelButton>
          <SaveButton onClick={handleSave} disabled={loading}>
            <FiSave size={16} />
            {loading ? 'Saving...' : 'Save Changes'}
          </SaveButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UserManagementModal; 